const UTM_KEYS = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'utm_term'] as const

export const UTM_FIRST_TOUCH_COOKIE = 'okidoki_utm_ft'
export const UTM_FIRST_TOUCH_MAX_AGE_SEC = 90 * 24 * 60 * 60

const MAX_VALUE_LEN = 200

export type UtmParams = {
  utm_source: string | null
  utm_medium: string | null
  utm_campaign: string | null
  utm_content: string | null
  utm_term: string | null
}

type CookieJar = {
  read: () => string
  write: (line: string) => void
}

function emptyUtm(): UtmParams {
  return {
    utm_source: null,
    utm_medium: null,
    utm_campaign: null,
    utm_content: null,
    utm_term: null,
  }
}

function hasUtm(utm: UtmParams): boolean {
  return UTM_KEYS.some(key => utm[key])
}

function clip(value: string | null): string | null {
  if (!value) return null
  const trimmed = value.trim().slice(0, MAX_VALUE_LEN)
  return trimmed || null
}

export function utmFromSearch(search: string): UtmParams | null {
  const params = new URLSearchParams(search.startsWith('?') ? search.slice(1) : search)
  const utm = emptyUtm()
  for (const key of UTM_KEYS) {
    utm[key] = clip(params.get(key))
  }
  return hasUtm(utm) ? utm : null
}

function encodePayload(utm: UtmParams): string {
  const bytes = new TextEncoder().encode(JSON.stringify(utm))
  let binary = ''
  for (let i = 0; i < bytes.length; i++) binary += String.fromCharCode(bytes[i])
  return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '')
}

function decodePayload(raw: string): UtmParams | null {
  try {
    const b64 = raw.replace(/-/g, '+').replace(/_/g, '/')
    const pad = b64.length % 4 === 0 ? '' : '='.repeat(4 - (b64.length % 4))
    const binary = atob(b64 + pad)
    const bytes = Uint8Array.from(binary, char => char.charCodeAt(0))
    const parsed = JSON.parse(new TextDecoder().decode(bytes))
    if (!parsed || typeof parsed !== 'object') return null
    const utm = emptyUtm()
    for (const key of UTM_KEYS) {
      utm[key] = typeof parsed[key] === 'string' ? clip(parsed[key]) : null
    }
    return hasUtm(utm) ? utm : null
  } catch {
    return null
  }
}

function readNamedCookie(header: string, name: string): string | null {
  for (const part of header.split(';')) {
    const trimmed = part.trim()
    const eq = trimmed.indexOf('=')
    if (eq === -1) continue
    if (trimmed.slice(0, eq) === name) return trimmed.slice(eq + 1)
  }
  return null
}

function browserJar(): CookieJar | null {
  if (typeof document === 'undefined') return null
  return {
    read: () => document.cookie,
    write: (line: string) => {
      document.cookie = line
    },
  }
}

function cookieLine(payload: string, secure: boolean): string {
  const parts = [
    `${UTM_FIRST_TOUCH_COOKIE}=${payload}`,
    'Path=/',
    `Max-Age=${UTM_FIRST_TOUCH_MAX_AGE_SEC}`,
    'SameSite=Lax',
  ]
  if (secure) parts.push('Secure')
  return parts.join('; ')
}

// Первая метка не перезаписывается: повторный заход с другим UTM оставляет исходную.
export function captureUtmFirstTouch(options?: {
  search?: string
  jar?: CookieJar
  secure?: boolean
}): UtmParams | null {
  const jar = options?.jar ?? browserJar()
  if (!jar) return utmFromSearch(options?.search ?? '')

  const existing = decodePayload(readNamedCookie(jar.read(), UTM_FIRST_TOUCH_COOKIE) || '')
  if (existing) return existing

  const search = options?.search ?? (typeof window === 'undefined' ? '' : window.location.search)
  const fromUrl = utmFromSearch(search)
  if (!fromUrl) return null

  const secure = options?.secure ?? (
    typeof window !== 'undefined' && window.location?.protocol === 'https:'
  )
  jar.write(cookieLine(encodePayload(fromUrl), secure))
  return fromUrl
}
